import {
    SHIP_TO_ME,
    SHIP_TO_STORE
} from '../enums/bopisEnums';

import { debug } from '../../../core/js/utils/logger';

/**
 * Is BOPIS inventory disabled due to nullified values?
 * @param  {object}  pool  Inventory pool object
 * @return {boolean}      True, if BOPIS is disabled; false, otherwise.
 */
export function isBopisDisabled(pool) {
    return pool && pool.ispuStockLevel === null && pool.stsStockLevel === null;
}

/**
 * Is the inventory eligible for in store pick up?
 * @param  {object}  pool       Inventory pool object
 * @param  {number}  quantity   Selected sku quantity
 * @return {boolean}            True, if is in store pick up; false, otherwise.
 */
export function isIspu(pool, quantity) {
    return pool && pool.ispuStockLevel >= quantity;
}

/**
 * Is the inventory eligible to be shipped to store?
 * @param  {object}  pool       Inventory pool object
 * @param  {number}  quantity   Selected sku quantity
 * @return {boolean}            True, if ship to store; false, otherwise.
 */
export function isSts(pool, quantity) {
    return pool && !isIspu(pool, quantity) && pool.stsStockLevel >= quantity;
}

/**
 * Is the inventory in stock?
 * @param  {object}  pool       Inventory pool object
 * @param  {number}  quantity   Selected sku quantity
 * @return {boolean}            True, if in stock; false, otherwise.
 */
export function isInStock(pool, quantity) {
    return pool &&
            !isIspu(pool, quantity) &&
            !isSts(pool, quantity) &&
            pool.retailStockLevel >= quantity;
}

/**
 * Is the inventory low in stock?
 * @param  {object}  pool       Inventory pool object
 * @param  {number}  quantity   Selected sku quantity
 * @return {boolean}            True, if low in stock; false, otherwise.
 */
export function isLowInStock(pool, quantity) {
    return pool &&
            !isIspu(pool, quantity) &&
            !isSts(pool, quantity) &&
            pool.retailStockLevel < 5;
}

/**
 * Is the inventory out of stock?
 * @param  {object}  pool       Inventory pool object
 * @param  {number}  quantity   Selected sku quantity
 * @return {boolean}            True, if out of stock; false, otherwise.
 */
export function isOutOfStock(pool, quantity) {
    return pool &&
            !isIspu(pool, quantity) &&
            !isSts(pool, quantity) &&
            !isInStock(pool, quantity) &&
            !isLowInStock(pool, quantity);
}

export function isRequestedQuantityPartiallyAvailable(pool, quantity) {
    return pool &&
            pool.retailStockLevel > 0 &&
            pool.retailStockLevel < quantity;
}

/* eslint-disable complexity */
/**
 * Is the option to Ship to Store disabled in the UI?
 * @param  {object}  store      MISL store response
 * @param  {object}  pool       Inventory pool object
 * @param  {number}  quantity   Selected sku quantity
 * @return {boolean}            True, if Ship to Store is disabled; false, otherwise.
 */
export function isShipToStoreDisabled(store, pool, quantity) {
    return !store || !pool || isBopisDisabled(pool) || isInStock(pool, quantity) || isLowInStock(pool, quantity) || isRequestedQuantityPartiallyAvailable(pool, quantity);
}
/* eslint-enable complexity */

/**
 * Get the inventory pool for a given store from a given sku
 * @param  {object} store MISL store response or store number
 * @param  {object} sku   Product SKU
 * @return {object|null}  Returns the pool object if inventory exists; otherwise, null.
 */
export function getPoolFromSku(store, sku) {
    let pool;

    try {
        pool = sku.pools[_.get(store, 'storeNumber', store)];
    } catch (e) {
        debug('Unable to get target pool');
    }

    return pool || null;
}

/**
 * Is the option to Ship to Store disabled in the UI?
 * @param  {object}  store          MISL store response
 * @param  {number}  quantity       Selected sku quantity
 * @param  {object}  sku            Product sku
 * @return {boolean}                True, if Ship to Store is disabled; false, otherwise.
 */
export function getBopisState(store, quantity, sku) {
    const pool = getPoolFromSku(store, sku);
    const disableShipToStore = isShipToStoreDisabled(store, pool, quantity);

    return {
        productBopis: {
            disableShipToStore: disableShipToStore,
            enums: {
                shipToMe: SHIP_TO_ME,
                shipToStore: SHIP_TO_STORE
            },
            selectedPool: pool,
            bopisCallHasBeenMade: true
        }
    };
}
